import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Network, Shuffle } from "lucide-react";
import { Btn } from "./Btn";
import { Eyebrow } from "./Typo";

const NODES = [[70, 60], [150, 34], [235, 70], [320, 40], [405, 76], [490, 46], [110, 120], [200, 140], [290, 112], [380, 134], [470, 118], [540, 96]];
const LINKS = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [0, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [2, 8], [4, 10], [1, 7], [3, 9], [5, 11]];
const CURVES = ["M40 300 C 170 290, 260 250, 560 236", "M40 300 C 180 280, 300 230, 560 196", "M40 300 C 160 270, 320 200, 560 150",
  "M40 300 C 190 300, 330 280, 560 270", "M40 300 C 200 250, 300 170, 560 112"];
const BEST = 4;

export const SimulationVisual = () => (
  <div className="sim" data-reveal role="img" aria-label="Des agents IA testent plusieurs stratégies ; une seule est recommandée" data-testid="simulation-visual">
    <svg viewBox="0 0 600 360" aria-hidden="true">
      <defs>
        <linearGradient id="simGrad" x1="0" x2="1"><stop offset="0" stopColor="var(--magenta)" /><stop offset="0.5" stopColor="var(--violet)" /><stop offset="1" stopColor="var(--cyan)" /></linearGradient>
      </defs>
      <g className="sim-links">{LINKS.map(([a, b]) => <line key={`${a}-${b}`} x1={NODES[a][0]} y1={NODES[a][1]} x2={NODES[b][0]} y2={NODES[b][1]} />)}</g>
      <g className="sim-nodes">{NODES.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="6" style={{ "--i": i }} />)}</g>
      <g className="sim-bars">{[34, 52, 26, 64, 42, 70, 48].map((h, i) => <rect key={i} x={70 + i * 70} y={340 - h * 0.5} width="26" height={h * 0.5} rx="4" style={{ "--i": i }} />)}</g>
      <g className="sim-curves">{CURVES.map((d, i) => <path key={d} d={d} pathLength="1" className={i === BEST ? "best" : ""} style={{ "--i": i }} />)}</g>
      <circle className="sim-end" cx="560" cy="112" r="7" />
    </svg>
    <span className="sim-label" data-testid="simulation-recommended">Stratégie recommandée</span>
  </div>
);

export const STRATEGY_POINTS = [
  [Network, "Votre marché recréé", "Vos clients, vos concurrents, vos contraintes.", "magenta"],
  [Shuffle, "Des centaines de scénarios testés", "On compare les options, pas les intuitions.", "cyan"],
  [ClipboardList, "Un plan d’action clair", "Verdict, chiffre d’affaires prévisionnel, actions semaine par semaine.", "amber"],
];

export const StrategyPoints = ({ items = STRATEGY_POINTS }) => (
  <ul className="strat-points" data-testid="strategy-points">
    {items.map(([Icon, t, d, c], i) => (
      <li key={t} data-reveal style={{ "--d": `${i * 80}ms`, "--c": `var(--${c})` }}>
        <span><Icon size={18} aria-hidden="true" /></span><div><b>{t}</b><p>{d}</p></div>
      </li>
    ))}
  </ul>
);

export const ReportPreview = () => (
  <div className="report" data-reveal aria-label="Aperçu d’un rapport de simulation (exemple stylisé)" role="img" data-testid="report-preview">
    <div className="report-top"><i /><i /><i /><b>Rapport de simulation</b></div>
    {["Notre recommandation", "Ce que ça peut rapporter", "La première chose à faire"].map((l, i) => (
      <div className="report-row" key={l} style={{ "--i": i }}><span>{l}</span><i className="blur-bar" /><i className="blur-bar short" /></div>
    ))}
  </div>
);

export const FORMULAS = [["Express", "Une question, un verdict rapide."], ["Complète", "Étude approfondie + présentation."],
  ["Suivi mensuel", "La simulation recalée chaque mois avec vos vrais chiffres."]];

export const Formulas = () => (
  <div className="formulas" data-testid="strategy-formulas">
    {FORMULAS.map(([t, d], i) => (
      <article className="formula spot" key={t} data-reveal style={{ "--d": `${i * 80}ms` }} data-testid={`formula-${i}`}>
        <h3>{t}</h3><p>{d}</p><em>Sur devis</em>
      </article>
    ))}
  </div>
);

export const STRATEGY_TEXT = "Nous reproduisons votre marché avec des agents IA et comparons des centaines de scénarios. Vous savez quoi lancer, ce que ça peut rapporter et quoi faire, mois par mois — avant de dépenser le moindre euro.";

export const StrategySection = () => (
  <section className="section strat" aria-labelledby="strat-title" data-testid="home-strategy">
    <div className="strat-grid">
      <div data-reveal>
        <Eyebrow>STRATÉGIE PILOTÉE PAR L’IA</Eyebrow>
        <h2 id="strat-title">Testez votre stratégie <span className="grad-text">avant d’investir.</span></h2>
        <p className="lead">{STRATEGY_TEXT}</p>
        <StrategyPoints />
      </div>
      <div className="strat-visuals"><SimulationVisual /><ReportPreview /></div>
    </div>
    <Formulas />
    <div className="actions strat-actions" data-reveal>
      <Btn to="/contact/?offre=strategie#projet" testid="strategy-cta">Demander mon étude</Btn>
      <Link className="more-link" to="/strategie-ia/" data-testid="strategy-more">En savoir plus <ArrowRight size={16} aria-hidden="true" /></Link>
    </div>
  </section>
);

const JOURNEY = [["Audit offert", "On analyse votre présence en ligne.", "/apercu-boutique/?type=audit"], ["Simulation", "On teste votre stratégie."],
  ["Mise en œuvre", "Site, intégrations, agents IA."], ["Suivi", "On mesure et on ajuste chaque mois."]];

export const Journey = ({ headingLevel: H = "h2" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => el.style.setProperty("--p", e.intersectionRatio.toFixed(2)),
      { threshold: Array.from({ length: 21 }, (_, i) => i / 20), rootMargin: "0px 0px -25% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <section className="section journey" aria-labelledby="journey-title" data-testid="journey">
      <div className="section-head" data-reveal><Eyebrow>VOTRE PARCOURS</Eyebrow><H id="journey-title">Votre parcours avec <span className="grad-text">NovaSoft</span></H></div>
      <ol className="journey-line" ref={ref}>
        <span className="journey-track" aria-hidden="true"><i /></span>
        {JOURNEY.map(([t, d, to], i) => (
          <li key={t} style={{ "--i": i }} data-testid={`journey-step-${i + 1}`}>
            <span className="journey-dot">{String(i + 1).padStart(2, "0")}</span>
            <b>{to ? <Link to={to} data-testid="journey-audit-link">{t}</Link> : t}</b><p>{d}</p>
          </li>
        ))}
      </ol>
    </section>
  );
};
